// ============================================================
// MongoDBCrudDelete.jsx
//
// A branded, technical guide to deleting data in MongoDB.
// Covers deleteOne, deleteMany, and the Drop collection method.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #FF5252  (Cyber Red / Danger)
//   Accent      → #00ED64  (MongoDB Green / Safety)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
    Button
} from "@mui/material";

/* MUI Icons */
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import SecurityIcon from "@mui/icons-material/Security";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(255, 82, 82, 0.2)", // Red Border
    red: "#FF5252",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.red }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FFCDD2", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBCrudDelete() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.red }} />}
                        label="CRUD Operations · Module 08"
                        sx={{ bgcolor: "rgba(255,82,82,0.1)", color: BRAND.red, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Delete <span style={{ color: BRAND.red }}>Operations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Removing data is permanent. MongoDB provides precise methods to delete a single document,
                        multiple documents, or entire collections. Always <strong>filter twice, delete once.</strong>
                    </Typography>
                </Box>

                {/* 2. THE DELETE METHODS */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <DeleteForeverIcon sx={{ color: BRAND.red, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>deleteOne()</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Deletes the <strong>first</strong> document that matches the filter. This is most
                            commonly used with unique fields like <code>_id</code> to ensure only one
                            specific record is removed.
                        </Typography>
                        <Alert severity="error" sx={{ bgcolor: "rgba(255,82,82,0.05)", color: BRAND.white, border: `1px solid ${BRAND.red}` }}>
                            <Typography variant="body2">
                                <strong>Safety First:</strong> If you pass an empty filter <code>{"{}"}</code> to a delete method,
                                it will remove documents! (deleteOne removes the first one it finds).
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="mongosh: Target Delete"
                            code={`// Delete a specific user by ID\ndb.users.deleteOne({ _id: ObjectId("65f1a...") });\n\n// Delete the first inactive user found\ndb.users.deleteOne({ status: "inactive" });`}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. BULK DELETE */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
                        <CodeTerminal
                            label="mongosh: Bulk Cleanup"
                            code={`// Delete ALL users with status 'banned'\ndb.users.deleteMany({ status: "banned" });\n\n// Clear entire collection (Careful!)\ndb.users.deleteMany({});`}
                        />
                    </Grid>
                    <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <DeleteSweepIcon sx={{ color: BRAND.red, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>deleteMany()</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Used for batch operations. It removes <strong>all</strong> documents that match
                            your query criteria. This is useful for clearing logs, removing expired sessions,
                            or performing administrative cleanups.
                        </Typography>
                    </Grid>
                </Grid>

                {/* 4. THE PRO-TIP: SOFT DELETE */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: `1px solid ${BRAND.green}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <SecurityIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Pro-Tip: "Soft Deletes"</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        In professional production apps, we rarely use <code>deleteOne</code>. Instead, we use
                        <strong> Soft Deletes</strong> by adding a <code>isDeleted: true</code> flag.
                        This allows for data recovery and audit trails.
                    </Typography>

                    <CodeTerminal
                        label="The 'Professional' Delete"
                        color={BRAND.green}
                        code={`// Instead of deleting, just 'archive' it\ndb.users.updateOne(\n  { _id: user_id }, \n  { $set: { status: "deleted", deletedAt: new Date() } }\n);`}
                    />
                </Box>

                {/* 5. DROP VS DELETE */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <WarningAmberIcon sx={{ color: BRAND.red, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Nuclear Option: db.drop()</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        If you need to delete <strong>millions</strong> of documents, <code>deleteMany({})</code> is slow
                        because it removes docs one by one. Use <code>db.collection.drop()</code> to delete the entire
                        collection and its indexes instantly.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.red, fontWeight: 800 }}>db.collection.drop()</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Removes data AND indexes. Extremely fast.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: "1px solid rgba(255,255,255,0.1)" }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 800 }}>db.dropDatabase()</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Deletes the entire database and all collections within it.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #440000)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <RestartAltIcon style={{ fontSize: 40, color: BRAND.red, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>CRUD Mastered?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        You've learned Create, Read, Update, and Delete. Now, let's look at how to model
                        complex data structures for your application.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.red, color: BRAND.white, fontWeight: 900 }}>Go to Schema Design</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Review All CRUD</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
